import React, { useState } from "react";
import { StatusBadge } from "@/components/StatusBadge";
import { MarkPaidDialog } from "@/components/MarkPaidDialog";
import { ActivityDialog } from "@/components/ActivityDialog";
import { formatCents, shortDate } from "@/lib/format";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MoreHorizontal, FileText, Landmark, Repeat } from "lucide-react";

function ActionMenu({ record, onAction, onEdit, onMarkPaid, onActivity, onSnooze }) {
  const isInvoice = record.type === "invoice";
  const status = isInvoice ? record.lifecycle_status : record.status;
  const settled = ["paid", "cancelled", "archived"].includes(status);
  const isOverdue = record.display_status === "Overdue";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1.5 rounded-md hover:bg-oatmeal" data-testid={`actions-${record.reference_code}`}>
          <MoreHorizontal size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {!settled && (
          <DropdownMenuItem onClick={() => onMarkPaid(record)} data-testid={`action-markpaid-${record.reference_code}`}>
            {isInvoice ? "Mark paid" : "Mark received"}
          </DropdownMenuItem>
        )}
        {isInvoice && isOverdue && (
          <>
            <DropdownMenuItem onClick={() => onSnooze(record, 3)} data-testid={`action-snooze3-${record.reference_code}`}>Snooze 3 days</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSnooze(record, 7)} data-testid={`action-snooze7-${record.reference_code}`}>Snooze 7 days</DropdownMenuItem>
          </>
        )}
        {isInvoice && record.lifecycle_status === "draft" && (
          <DropdownMenuItem onClick={() => onAction(record, "mark-sent")}>Mark sent</DropdownMenuItem>
        )}
        {!isInvoice && record.status === "scheduled" && (
          <DropdownMenuItem onClick={() => onAction(record, "mark-processing")} data-testid={`action-processing-${record.reference_code}`}>Mark processing</DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onEdit(record)} data-testid={`action-edit-${record.reference_code}`}>Edit</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onActivity(record)} data-testid={`action-activity-${record.reference_code}`}>View activity</DropdownMenuItem>
        <DestructiveItem label="Archive" onConfirm={() => onAction(record, "archive")} record={record} />
        {!settled && <DestructiveItem label="Cancel" onConfirm={() => onAction(record, "cancel")} record={record} />}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DestructiveItem({ label, onConfirm, record }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-rose-600" data-testid={`action-${label.toLowerCase()}-${record.reference_code}`}>
          {label}
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{label} {record.reference_code}?</AlertDialogTitle>
          <AlertDialogDescription>
            This changes the record's status. It won't remove historical account events.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="confirm-destructive">{label}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function RecordsTable({ records, onAction, onEdit, onRefresh }) {
  const [paidRecord, setPaidRecord] = useState(null);
  const [paidOpen, setPaidOpen] = useState(false);
  const [activityRecord, setActivityRecord] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);

  const openMarkPaid = (r) => { setPaidRecord(r); setPaidOpen(true); };
  const openActivity = (r) => { setActivityRecord(r); setActivityOpen(true); };
  const snooze = async (r, days) => {
    try {
      await api.post(`/invoices/${r.id}/snooze`, { days });
      toast.success(`Snoozed ${r.reference_code} for ${days} days — we'll nudge you again`);
      onRefresh && onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const menuProps = { onAction, onEdit, onMarkPaid: openMarkPaid, onActivity: openActivity, onSnooze: snooze };

  const RefCell = ({ r, size = 14 }) => (
    <span className="inline-flex items-center gap-2 font-num font-semibold text-midnight">
      {r.type === "invoice" ? <FileText size={size} className="text-ink-muted" /> : <Landmark size={size} className="text-ink-muted" />}
      {r.reference_code}
      {r.recurring && (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-brandblue bg-brandblue-light px-1.5 py-0.5 rounded" data-testid={`recurring-badge-${r.reference_code}`}>
          <Repeat size={10} /> Monthly
        </span>
      )}
    </span>
  );

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block border rounded-2xl overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="records-table">
          <thead>
            <tr className="border-b bg-[#FBFCFD] text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-3.5">Reference</th>
              <th className="px-5 py-3.5">Source / Client</th>
              <th className="px-5 py-3.5 text-right">Amount</th>
              <th className="px-5 py-3.5">Due / Expected</th>
              <th className="px-5 py-3.5">Status</th>
              <th className="px-5 py-3.5 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} className="border-b last:border-0 border-[#F0F1F4] hover:bg-[#F7F8FA] transition-colors" data-testid={`row-${r.reference_code}`}>
                <td className="px-5 py-3.5">
                  <RefCell r={r} />
                </td>
                <td className="px-5 py-3.5 text-ink-secondary">{r.name}</td>
                <td className="px-5 py-3.5 text-right font-num font-bold text-midnight">{formatCents(r.amount_cents, r.currency)}</td>
                <td className="px-5 py-3.5 text-ink-muted">{shortDate(r.date)}</td>
                <td className="px-5 py-3.5"><StatusBadge status={r.display_status} daysOverdue={r.days_overdue} /></td>
                <td className="px-5 py-3.5"><ActionMenu record={r} {...menuProps} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="records-cards">
        {records.map((r) => (
          <div key={r.id} className="border rounded-2xl bg-white p-4 transition-transform duration-200 active:scale-[0.99]" data-testid={`card-${r.reference_code}`}>
            <div className="flex items-start justify-between">
              <div>
                <RefCell r={r} size={13} />
                <p className="text-sm text-ink-secondary mt-0.5">{r.name}</p>
              </div>
              <ActionMenu record={r} {...menuProps} />
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="font-num font-bold text-xl text-midnight tracking-tight">{formatCents(r.amount_cents, r.currency)}</span>
              <StatusBadge status={r.display_status} daysOverdue={r.days_overdue} />
            </div>
            <p className="text-xs text-ink-muted mt-2">Due {shortDate(r.date)}</p>
          </div>
        ))}
      </div>

      <MarkPaidDialog record={paidRecord} open={paidOpen} onOpenChange={setPaidOpen} onDone={onRefresh} />
      <ActivityDialog record={activityRecord} open={activityOpen} onOpenChange={setActivityOpen} />
    </>
  );
}
